<template>
    <Rows-container 
        ref="rowsContainer" 
        :items-props="partLines"
        @on-down="onDownRows"
    >
        <template v-for="(item, index) in partLines" :key="item.lineId">
            <line-view-skeleton v-if="item.cardItems === null" :name="item.skeleton" />
            <row-view
                v-if="checkExist(item)"
                :ref="item.ref"
                :index-row="index"
                :key-data="item.keyData"
                :item="item"
                :items="item.cardItems"
                :line-id="item.lineId"
                :title="item.title"
                @loaded-line="onLoadedLine"
                @list-item-selected="itemsSelected"
                @show-all="showAll"
                @plus="onPlus"
            />
            
        </template>
    </Rows-container>
</template>

<script lang="ts" setup>
    import { ref, type Ref, computed, type ComputedRef, watch, nextTick } from "vue"
    import { useI18n } from "vue-i18n";
    import { Strings } from "@/locales/types/enums";
    import { AppConstants } from "@/common";
    import type { RowsContainerRef } from "@/components/RowsContainer/types";
    import RowsContainer from "@/components/RowsContainer/RowsContainer.vue";
    import { useParentControl } from "@/composables/parentControl/useParentControl";
    import { useLinesMyTvStore } from "@/stores/LinesMyTv";
    import type { RowConfig, IEventSelected } from "@/components/RowView/types";
    import { storeToRefs } from 'pinia';
    import LineViewSkeleton from "@/components/Skeleton/LineViewSkeleton.vue";
    import { useParentControlStore } from "@/stores/ParentControl";
    import type { ExposeInterface } from "./types";
    import { ComponentName as SkeletonName } from "@/components/Skeleton/types";


    const ParentControlStoreModule = useParentControlStore();
    const {
        ShowParentModal,
        SetTargetWidgetAdultControl,
        SetIncorrectAdultPin,
    } = ParentControlStoreModule;
    const {
        getIsAdultPinCorrect: isAdultPinCorrect,
        getTargetWidgetAdultControl: targetWidgetAdultControl,
    } = storeToRefs(ParentControlStoreModule);

    const LinesMyTvStore = useLinesMyTvStore();
    const {
        getCounterLinesMyTv,
        getCardsOnDemand,
        getCardsPrograms,
        getCardsChannels
    } = storeToRefs(LinesMyTvStore);

    const { increaseCounterLinesMyTv } = LinesMyTvStore;

    const emit = defineEmits(['show-all', 'list-item-selected', 'ready', 'plus']);
    const { t } = useI18n();
    const selectedItem: Ref<IEventSelected | null> = ref(null);

    const rowsContainer: Ref<RowsContainerRef | null> = ref(null); 
    const { setCurrentTimes, checkParentControlCard } = useParentControl();
    const name = "LinesMyTv";
    const { shelfKeys }: typeof AppConstants = AppConstants;

    const allLines: ComputedRef<RowConfig[]> = computed(() => {
        return [
            {
                keyData: shelfKeys.favouritesChannelsCards, 
                title: t(Strings.MY_TV_FAVORITE_CHANNELS).toString(),
                ref: shelfKeys.favouritesChannelsCards,
                lineId: shelfKeys.favouritesChannelsCards,
                skeleton: SkeletonName.PROGRAM_CARD_SKELETON,
                cardItems: getCardsChannels.value,
            },
            {
                keyData: shelfKeys.favouritesOnDemandsCards,
                title: t(Strings.MY_TV_FAVORITE_ON_DEMAND).toString(),
                ref: shelfKeys.favouritesOnDemandsCards,
                lineId: shelfKeys.favouritesOnDemandsCards,
                skeleton: SkeletonName.MOVIE_CARD_SKELETON,
                cardItems: getCardsOnDemand.value,
            },
            {
                keyData: shelfKeys.favouritesLiveTvCards,
                title: t(Strings.MY_TV_MY_WATCH_LIST).toString(),
                ref: shelfKeys.favouritesLiveTvCards,
                lineId: shelfKeys.favouritesLiveTvCards,
                skeleton: SkeletonName.PROGRAM_CARD_SKELETON,
                cardItems: getCardsPrograms.value,
            }
        ];
    });
    const partLines: ComputedRef<RowConfig[]> = computed(() => allLines.value.slice(0, getCounterLinesMyTv.value));

    const checkExist = (item: RowConfig): boolean => {
        const isExist = item.cardItems !== null && item.cardItems.length > 0;
        if (!isExist) {
            onLoadedLine();
        } 
        return isExist;
    };
    
    const counter = ref(1);
    const onLoadedLine = (): void => {
        if (rowsContainer.value === null) {
            return;
        }
        const countLoadedRows = rowsContainer.value.getCountLoadedRows();
        if (countLoadedRows !==null && getCounterLinesMyTv.value >= countLoadedRows) {
            nextTick(() => {
                emit('ready');
            });
        }

        if (counter.value <= AppConstants.HomeCounterLines) {
            const isIncreasing = (counter.value % AppConstants.HomeCounterLines) != 0; 
            if (isIncreasing) {
                increaseCounterLinesMyTv();
                counter.value++;
            }
        }
    };

    const onPlus = (lineId: string): void => {
        emit("plus", lineId);
    };

    const onListItemSelected = (): void => {
        emit("list-item-selected", selectedItem.value);
    };
    const itemsSelected = (event: IEventSelected): void => {
        selectedItem.value = event;

        checkParentControlCard(event).then((isAllow)=>{
            if (isAllow) {
                onListItemSelected();
            } else {
                ShowParentModal();
                SetTargetWidgetAdultControl(name);
            }
        });
    };

    const onDownRows = (): void => {
        if (getCounterLinesMyTv.value < allLines.value.length) {
            increaseCounterLinesMyTv();
        }
    };

    const showAll = (event: IEventSelected): void => {
        emit("show-all", event);
    };
    const focusFirstRow = (): void => {
        if (rowsContainer.value) {
            rowsContainer.value.focusFirstRow();
        }
    };

    watch(
        isAdultPinCorrect, 
        (value: boolean): void => {
            if (value && name === targetWidgetAdultControl.value) {
                SetIncorrectAdultPin();
                setCurrentTimes();
                onListItemSelected();
            }
        }
    );

    defineExpose<ExposeInterface>({
        focusFirstRow
    });
</script>